import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox/';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import messages from './messages';
import styles from './styles.module.less';

/**
 * @typedef {import('../../../../../prop-types/intl-types').MessageDescriptor} MessageDescriptor
 */

/**
 * Permission component for displaying and managing user permissions
 * @param {object} props - Component props
 * @param {string} props.id - Unique identifier for the permission
 * @param {MessageDescriptor} props.label - Message descriptor for the permission label
 * @param {MessageDescriptor} props.details - Message descriptor for the permission details
 * @param {boolean} [props.isChecked] - Whether the permission is checked
 * @param {(id: string, value: boolean) => void} props.onChange - Callback when permission is toggled
 * @param {boolean} [props.disabled] - Whether the permission is disabled
 * @param {MessageDescriptor} [props.tooltipMessage] - Message descriptor for the tooltip when disabled
 * @returns {JSX.Element} Permission component
 */
export const Permission = ({
  id,
  label,
  details,
  isChecked = false,
  onChange,
  disabled = false,
  tooltipMessage = messages.cannotEditSelf,
}) => {
  const intl = useIntl();
  const CheckboxAndLabel = (
    <div className={styles.perm}>
      <Checkbox
        checked={isChecked}
        className={styles.checkbox}
        disabled={disabled}
        inputId={id}
        label={intl.formatMessage(label)}
        onChange={(value) => onChange(id, value)}
        // TODO SPD-20213: Add focus back when the checkbox supports toggling by keyboard
        // tabIndex={0}
      />
      <FormattedMessage {...details} />
    </div>
  );

  if (!disabled) {
    return CheckboxAndLabel;
  }

  return (
    <Tooltip>
      {CheckboxAndLabel}
      <TooltipContent>
        <div>
          <FormattedMessage {...tooltipMessage} />
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
